import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CaseInstructionSummary } from '../../atoms/case-instruction-summary/CaseInstructionSummary';
import { StageStatusProgress } from '../../molecules/stage-status-progress/StageStatusProgress';
import { CaseType } from 'modules/case-tracking/types/case-tracking.types';
import styles from './instruction.module.scss';
import { useNavigate } from 'react-router-dom';
import { routes } from 'routes/route-paths';
import { Button, Icon, ICONS_ENUM, Paper, Typography } from '@sanlam-glacier/app-common/components';
import InstructionTab from '../../organisms/instruction-tab/InstructionTab';
import { mockCaseData, mockDocumentsRequested } from 'stores/case-tracking-mock-data';
import { DocumentsRequired } from 'modules/case-tracking/types/documents-required/documents-required.types';
import { documentTimestamp } from 'modules/case-tracking/utils/document-timestamp.function';
import { downloadDocumentBackgroundFromBlob } from 'utils/background-promises/application-download';
import { useGetCaseTrackingHistory } from 'bff/hooks/reporting/CaseTrackingHistory';
import { useState } from 'react';

// Todo - Remove this mock function when integrating with real API
const fetchCaseDetails = async (instructionId?: string) => {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  const allCases = mockCaseData;

  const caseDetails = allCases.find((c: CaseType) => String(c.instructionNumber) === instructionId);

  if (!caseDetails) {
    throw new Error(`No instruction found for ID ${instructionId}`);
  }
  return caseDetails;
};

// Todo - Remove this mock function when integrating with real API
const fetchDocumentsRequested = async (instructionId?: string): Promise<DocumentsRequired[]> => {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  const allDocs = mockDocumentsRequested;

  const documentsRequested = allDocs.filter(
    (document: DocumentsRequired) => String(document.instructionId) === instructionId
  );

  if (!documentsRequested) {
    return [];
  }
  return documentsRequested;
};

const Instruction = () => {
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const handleBackButton = () => {
    navigate(`/${routes.caseTracking}`);
  };

  const {
    data: caseDetails,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['caseDetails', instructionId],
    queryFn: () => fetchCaseDetails(instructionId),
    enabled: !!instructionId
  });

  const {
    data: documentsRequested,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError
  } = useQuery({
    queryKey: ['documentsRequested', instructionId],
    queryFn: () => fetchDocumentsRequested(instructionId)
  });

  const fileName = () => {
    return `Case Tracking_ ${caseDetails?.clientName}_ ${caseDetails?.instructionType}_ ${caseDetails?.instructionCategory}_ ${documentTimestamp()}`;
  };

  const { mutateAsync: downloadCaseHistory, isPending: isDownloading } =
    useGetCaseTrackingHistory();

  const [isActivityHistoryTab, setIsActivityHistoryTab] = useState<boolean>(true);

  if (isLoading || isDocumentsLoading) return <div>Loading case details...</div>;
  if (isError || isDocumentsError) return <div>Error loading case: {(error as Error).message}</div>;
  if (!caseDetails || !documentsRequested) return <div>No case details found.</div>;

  return (
    <>
      <div className={styles.headingContainer}>
        <Typography
          className={styles.heading}
          variant={Typography.VARIANT.H3}
          weight={Typography.WEIGHT.BOLD}
        >
          Instruction #{instructionId}
        </Typography>
        <Button
          className={styles.backButton}
          startIcon={<Icon icon={ICONS_ENUM.NAVIGATION_ARROW_BACK} />}
          variant={Button.VARIANT.TERTIARY}
          size={Button.SIZE.SMALL}
          onClick={handleBackButton}
        >
          Back to case tracking overview
        </Button>
      </div>
      <Paper className={styles.pageContainer}>
        <div className={styles.summaryContainer}>
          <CaseInstructionSummary caseDetails={caseDetails} />
          <StageStatusProgress {...caseDetails} />
        </div>
        <div className={styles.tabContainer}>
          <InstructionTab
            handleTabChange={setIsActivityHistoryTab}
            isCancelled={caseDetails.isCancelled}
            isDocumentsRequired={documentsRequested.length > 0}
            documents={documentsRequested}
            instructionId={String(caseDetails.instructionNumber)}
            clientName={caseDetails.clientName}
            caseDetails={caseDetails}
          />
          {isActivityHistoryTab && (
            <Button
              data-testid='caseTrackingDownloadCaseHistory'
              disabled={isDownloading}
              className={styles.downloadButton}
              startIcon={<Icon icon={ICONS_ENUM.DOCUMENTS_EXCEL_DOCUMENT} />}
              variant={Button.VARIANT.SECONDARY}
              size={Button.SIZE.SMALL}
              onClick={() => {
                downloadDocumentBackgroundFromBlob({
                  downloadFn: async () => {
                    const result = await downloadCaseHistory();
                    if (!result) return;
                    return {
                      blob: result,
                      fileName: fileName()
                    };
                  },
                  wording: {
                    message:
                      'Case tracking history Excel sheet will be available in your downloads folder when the download is complete.',
                    title: 'Excel sheet downloading...',
                    successMessage: 'Case Tracking history Excel sheet successfully downloaded.',
                    errorMessage: 'Case Tracking history Excel sheet download failed.',
                    promiseCompleteTitle: 'Excel document download'
                  },
                  toastId: 'caseTracking:caseHistory'
                });
              }}
            >
              Download Excel
            </Button>
          )}
        </div>
      </Paper>
    </>
  );
};

export default Instruction;
